/* Dependencies */
import anime from 'animejs';

// Models
import {
  FindYourFlavorItem,
  FindYourFlavorProps,
} from './FindYourFlavor.model';

/**
 * Find Your Flavor
 * @class
 */
class FindYourFlavor {
  public parentElement: HTMLElement;
  public items: FindYourFlavorItem[];
  public activeItem: FindYourFlavorItem;
  public changeColor = false;

  constructor(props: FindYourFlavorProps) {
    this.parentElement = props.parentElement;
    this.items = props.items;
    this.changeColor = props.changeColor;

    // Set the default slide.
    this.showContent(props.currentDisplayIndex, true);

    // Attach events
    this.bindEvents();
  }

  /**
   * Show the desired content.
   * @param newIndex - The index to be rendered.
   * @param preventAnimation - Prevent the animation.
   */
  showContent(newIndex: number, preventAnimation?: boolean): void {
    const desiredItem = this.items[newIndex];

    // Ensure not the current active item.
    if (this.activeItem === desiredItem) return;

    // Hide the previous item
    if (this.activeItem) {
      this.hideContent(this.activeItem);
    }

    // Update State
    this.activeItem = desiredItem;

    // Update the aria state
    desiredItem.triggerElement.setAttribute('aria-expanded', 'true');

    // Update color
    if (this.changeColor) {
      this.parentElement.classList.add(
        `tw-bg-${desiredItem.containerElement.getAttribute(
          'data-find-your-flavor-color'
        )}`
      );
    }

    // Apply styles without animation
    if (preventAnimation) {
      desiredItem.contentElement.classList.remove('tw-hidden');
      desiredItem.contentElement.classList.add('tw-block');
      desiredItem.containerElement.setAttribute('data-active', 'true');
      return;
    }

    // Animate in the new element
    anime({
      begin: () => {
        desiredItem.contentElement.classList.remove('tw-hidden');
        desiredItem.contentElement.classList.add('tw-opacity-0');
        desiredItem.contentElement.classList.add('tw-block');
      },
      targets: desiredItem.contentElement,
      opacity: [0, 1],
      duration: 200,
      easing: 'linear',
      complete: () => {
        desiredItem.containerElement.setAttribute('data-active', 'true');
      },
    });
  }

  /**
   * Hide the row
   */
  hideContent(activeItem: FindYourFlavorItem): void {
    // Update the state
    activeItem.containerElement.setAttribute('data-active', 'false');
    this.parentElement.classList.remove(
      `tw-bg-${activeItem.containerElement.getAttribute(
        'data-find-your-flavor-color'
      )}`
    );

    // Remove the content element
    activeItem.contentElement.classList.remove('tw-block');
    activeItem.contentElement.classList.add('tw-hidden');

    // Update the aria state
    activeItem.triggerElement.setAttribute('aria-expanded', 'false');
  }

  /**
   * Binds the related events for the section.
   */
  bindEvents(): void {
    this.items.forEach((item, index) => {
      item.triggerElement.addEventListener('click', (event) => {
        this.showContent(index);
      });
    });
  }
}

/**
 * Initiates the section.
 * @returns
 */
export const findYourFlavorInit = (): void => {
  // Detect if elements are present.
  if (!document.querySelector('[data-find-your-flavor]')) return;

  // Get all section elements.
  const findYourFlavorElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[data-find-your-flavor]')
  );

  // Loop and create section instances.
  findYourFlavorElements.forEach((findYourFlavorElement) => {
    // Retrieve additional elements
    const containerElements: HTMLElement[] = Array.from(
      findYourFlavorElement.querySelectorAll('[data-find-your-flavor-item]')
    );

    // Retrieve config
    const currentDisplayIndex = findYourFlavorElement.getAttribute(
      'data-find-your-flavor-display'
    )
      ? +findYourFlavorElement.getAttribute('data-find-your-flavor-display')
      : 0;
    const changeColor =
      findYourFlavorElement.getAttribute('data-find-your-flavor-color') !== null
        ? true
        : false;

    // Create FindYourFlavor.
    new FindYourFlavor({
      parentElement: findYourFlavorElement,
      items: containerElements.map((containerElement) => {
        return {
          containerElement: containerElement,
          triggerElement: containerElement.querySelector(
            '[data-find-your-flavor-trigger]'
          ) as HTMLElement,
          contentElement: containerElement.querySelector(
            '[data-find-your-flavor-content]'
          ) as HTMLElement,
        };
      }),
      currentDisplayIndex,
      changeColor,
    });
  });
};
